/**
 * TransactionHistoryTable — paginated credit transaction list.
 *
 * A15-N7. Loads 25 per page with "Mehr laden" button.
 */

import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../lib/apiClient';

const PAGE_SIZE = 25;

const SOURCE_LABELS = {
  stripe_topup: { icon: '💳', key: 'credits.source_topup', fallback: 'Aufgeladen' },
  ai_call:      { icon: '🤖', key: 'credits.source_ai_call', fallback: 'KI-Anfrage' },
  refund:       { icon: '↩',  key: 'credits.source_refund', fallback: 'Erstattung' },
  admin_grant:  { icon: '🎁', key: 'credits.source_admin_grant', fallback: 'Gutschrift' },
  expiration:   { icon: '⏱',  key: 'credits.source_expiration', fallback: 'Ablauf' },
};

export default function TransactionHistoryTable() {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState(null);

  const loadPage = useCallback(async (currentOffset, append = false) => {
    setLoading(true);
    try {
      const res = await apiClient.get(
        `/api/v1/aif/credits/transactions?limit=${PAGE_SIZE}&offset=${currentOffset}`
      );
      const data = res?.data ?? res;
      if (append) {
        setTransactions(prev => [...prev, ...(data.transactions || [])]);
      } else {
        setTransactions(data.transactions || []);
      }
      setHasMore(data.has_more);
    } catch (err) {
      setError(err.message || t('credits.load_error', 'Fehler beim Laden'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPage(0, false);
  }, [loadPage]);

  function loadMore() {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    loadPage(newOffset, true);
  }

  function formatDate(iso) {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  }

  function getSourceLabel(source) {
    const entry = SOURCE_LABELS[source];
    if (!entry) return source;
    return `${entry.icon} ${t(entry.key, entry.fallback)}`;
  }

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center h-20" data-testid="txn-loading">
        <div className="w-6 h-6 border-2 border-[var(--aif-border-default)] border-t-[var(--aif-color-primary)] rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-[var(--aif-color-danger-light)] p-3 rounded-xl text-[var(--aif-color-danger)] text-sm" data-testid="txn-error">
        {error}
      </div>
    );
  }

  if (transactions.length === 0) {
    return (
      <div
        className="text-[var(--aif-text-muted)] p-8 text-center border-2 border-dashed border-[var(--aif-border-default)] rounded-xl text-sm"
        data-testid="txn-empty"
      >
        {t('credits.no_transactions', 'Noch keine Transaktionen. Laden Sie Credits auf, um zu beginnen.')}
      </div>
    );
  }

  return (
    <div data-testid="txn-table">
      <div className="overflow-x-auto rounded-xl border border-[var(--aif-border-default)]">
        <table className="w-full text-sm">
          <thead className="bg-[var(--aif-bg-surface)]">
            <tr>
              <th className="text-left p-3 font-semibold text-[var(--aif-text-secondary)] text-xs">
                {t('credits.col_date', 'Datum')}
              </th>
              <th className="text-left p-3 font-semibold text-[var(--aif-text-secondary)] text-xs">
                {t('credits.col_type', 'Typ')}
              </th>
              <th className="text-right p-3 font-semibold text-[var(--aif-text-secondary)] text-xs">
                {t('credits.col_credits', 'Credits')}
              </th>
              <th className="text-right p-3 font-semibold text-[var(--aif-text-secondary)] text-xs">
                {t('credits.col_balance', 'Saldo')}
              </th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx) => (
              <tr
                key={tx.id}
                className="border-t border-[var(--aif-border-default)] hover:bg-[var(--aif-bg-surface)] transition"
                data-testid="txn-row"
              >
                <td className="p-3 text-[var(--aif-text-muted)] text-xs">{formatDate(tx.occurred_at)}</td>
                <td className="p-3 text-[var(--aif-text-primary)] text-xs">{getSourceLabel(tx.source)}</td>
                <td className={`p-3 text-right font-medium text-xs ${
                  tx.amount_credits >= 0
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-[var(--aif-color-danger)]'
                }`}>
                  {tx.amount_credits >= 0 ? '+' : ''}
                  {tx.amount_credits.toLocaleString('de-DE')}
                </td>
                <td className="p-3 text-right text-[var(--aif-text-secondary)] text-xs">
                  {tx.balance_after.toLocaleString('de-DE')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && (
        <div className="mt-4 text-center">
          <button
            onClick={loadMore}
            disabled={loading}
            className="text-[var(--aif-color-primary)] hover:opacity-75 text-sm font-medium disabled:opacity-50"
            data-testid="txn-load-more"
          >
            {loading
              ? t('common.loading', 'Lädt...')
              : t('credits.load_more', 'Mehr laden')
            }
          </button>
        </div>
      )}
    </div>
  );
}
